import React from 'react';
import StatusBadge from './StatusBadge';
import { statusBadge } from './statusTone';
import './EntityCard.css';
import mediaUrl from "../../utils/mediaUrl";

/**
 * One flat card for a team, a player or a league entry (demo-ready PR 4):
 * a title row (logo, name, badges) and plain rows, with no panel inside the
 * card. Lists render one EntityCard per item; PR 7 reuses it.
 *
 * @param {Object}   props
 * @param {string}   props.title
 * @param {string}   [props.logoUrl]
 * @param {string}   [props.logoAlt]
 * @param {Array<{label?: string, tone?: 'neutral'|'sport'|'success'|'warning'|'danger'|'muted', pulse?: boolean, title?: string, status?: string, kind?: 'match'}>} [props.badges]
 *        a badge with `status` takes its label and tone from the shared status map
 * @param {Array<{key?: string, icon?: React.ReactNode, label?: string, value: React.ReactNode}>} [props.rows]
 * @param {React.ReactNode} [props.corner]  e.g. a remove button, top right
 * @param {React.ReactNode} [props.feature] full-width content under the badges, e.g. a matchup
 * @param {React.ReactNode} [props.actions] buttons under the rows
 * @param {Function} [props.onClick]        opens the item; Enter/Space too
 * @param {string}   [props.ariaLabel]
 * @param {string}   [props.className]
 * @param {string}   [props.testId]
 */
const EntityCard = ({
  title,
  logoUrl,
  logoAlt,
  badges = [],
  rows = [],
  corner = null,
  feature = null,
  actions = null,
  onClick,
  ariaLabel,
  className = '',
  testId = 'entity-card',
}) => {
  const clickable = typeof onClick === 'function';
  const handleKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick(event);
    }
  };

  return (
    <article
      className={['entity-card', clickable ? 'entity-card--clickable' : '', className].filter(Boolean).join(' ')}
      data-testid={testId}
      onClick={clickable ? onClick : undefined}
      onKeyDown={clickable ? handleKeyDown : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-label={ariaLabel || title}
    >
      <header className="entity-card__head">
        {mediaUrl(logoUrl) && (
          <img
            className="entity-card__logo"
            src={mediaUrl(logoUrl)}
            alt={logoAlt || `${title} logo`}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        <h3 className="entity-card__title" title={title}>{title}</h3>
        {corner}
      </header>

      {badges.length > 0 && (
        <div className="entity-card__badges">
          {badges.filter(Boolean).map((raw) => {
            const badge = raw.status ? { ...statusBadge(raw.status, { kind: raw.kind }), ...raw } : raw;
            const tone = badge.tone || 'neutral';
            return (
              <StatusBadge
                key={badge.label}
                badge={{ ...badge, tone }}
                className={`entity-card__badge entity-card__badge--${tone}`}
              />
            );
          })}
        </div>
      )}

      {feature && <div className="entity-card__feature">{feature}</div>}

      {rows.length > 0 && (
        <dl className="entity-card__rows">
          {rows.map((row, index) => (
            <div className="entity-card__row" key={row.key || row.label || index}>
              <dt className="entity-card__label">
                {row.icon && <span className="entity-card__icon" aria-hidden="true">{row.icon}</span>}
                {row.label}
              </dt>
              <dd className="entity-card__value">{row.value}</dd>
            </div>
          ))}
        </dl>
      )}

      {actions && (
        <div className="entity-card__actions">
          {actions}
        </div>
      )}
    </article>
  );
};

export default EntityCard;
